/**
 * ConsentConfirmPage — /consent/:token
 * ─────────────────────────────────────────────────────────────────
 * League gap-closure Phase E (2026-06-09). Landing page for the
 * guardian-consent email link. Anonymous (the 256-bit token is the
 * credential; backend stores only its hash). Shows who/what the
 * consent covers, then a single explicit "I consent" action — the
 * click timestamp + IP become the league's consent record.
 *
 * Light styling to match the public-league surface (PublicRegister
 * uses the same palette) — this page is often opened from an email
 * on a phone by someone who has never seen the app.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import publicApi from '../../services/publicApi.js';

export default function ConsentConfirmPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);   // GET payload
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/registration-consent/${token}`)
      .then(({ data }) => { if (!cancelled) setInfo(data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || 'This consent link is invalid.');
      });
    return () => { cancelled = true; };
  }, [token]);

  async function confirm() {
    setBusy(true);
    try {
      await publicApi.post(`/registration-consent/${token}/confirm`);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not record consent.');
    } finally {
      setBusy(false);
    }
  }

  const card = (children) => (
    <div style={{ maxWidth: 560, margin: '48px auto', padding: '0 20px' }}>
      <div style={{
        background: '#fff', border: '1px solid #e2e6f0', borderRadius: 12,
        padding: 24, boxShadow: '0 1px 2px rgba(12,17,41,0.06)',
        color: '#0c1129', fontSize: 15, lineHeight: 1.55,
      }}>
        {children}
      </div>
    </div>
  );

  if (error) {
    return card(
      <>
        <AlertCircle size={26} color="#b45309" />
        <h1 style={h1}>Consent link problem</h1>
        <p>{error}</p>
        <p style={{ color: '#7a82a0', fontSize: 13 }}>
          You can request a fresh email from the registration page, or contact the league.
        </p>
      </>
    );
  }
  if (!info) return card(<>Loading…</>);

  if (done || info.status === 'confirmed') {
    return card(
      <>
        <CheckCircle2 size={28} color="#059669" />
        <h1 style={h1}>Consent recorded</h1>
        <p>
          Thank you — your consent for <strong>{info.minor_name}</strong>'s registration
          {info.config_name ? <> to <strong>{info.config_name}</strong></> : null}
          {info.league_name ? <> ({info.league_name})</> : null} is on file.
          The league can now confirm the registration.
        </p>
        <p style={{ marginTop: 16 }}>
          <Link to="/" style={{ color: '#0ea5e9' }}>Go to Game Day</Link>
        </p>
      </>
    );
  }

  if (info.status === 'expired') {
    return card(
      <>
        <AlertCircle size={26} color="#b45309" />
        <h1 style={h1}>Link expired</h1>
        <p>
          This consent link has expired. Sign in and open the registration page to send
          yourself a fresh one, or contact the league.
        </p>
      </>
    );
  }

  return card(
    <>
      <ShieldCheck size={28} color="#0ea5e9" />
      <h1 style={h1}>Guardian consent</h1>
      <p>
        You{info.guardian_name ? <> (<strong>{info.guardian_name}</strong>)</> : null} registered{' '}
        <strong>{info.minor_name}</strong>
        {info.config_name ? <> for <strong>{info.config_name}</strong></> : null}
        {info.league_name ? <> in <strong>{info.league_name}</strong></> : null}.
      </p>
      <p>
        Because the player is under the league's age of consent, the league requires a
        parent or guardian to explicitly confirm this registration. By clicking below you
        confirm you are the player's {info.relationship || 'guardian'} and consent to
        their participation under the registration's waivers.
      </p>
      <button
        type="button" onClick={confirm} disabled={busy}
        style={{
          marginTop: 12, background: '#0ea5e9', color: '#fff', border: 'none',
          padding: '12px 20px', borderRadius: 8, fontWeight: 700, fontSize: 15,
          cursor: busy ? 'wait' : 'pointer',
        }}>
        {busy ? 'Recording…' : 'I consent to this registration'}
      </button>
    </>
  );
}

const h1 = { fontSize: 24, fontWeight: 800, margin: '10px 0 12px', letterSpacing: '-0.01em' };
